import React, { useCallback, useEffect, useRef, useState } from "react";
import { Bell, BookOpen, ChevronUp, PanelRightClose, Plus, Trash2, Zap } from "lucide-react";
import { ICON_CONFIG } from "../config";
import AccountMenu from "./AccountMenu";

export default function Sidebar({
  user,
  isDark,
  sidebarOpen,
  setSidebarOpen,
  handleNewChat,
  sessions = [],
  activeSessionId,
  pendingSessionIds = new Set(),
  unreadSessionIds = new Set(),
  loadSession,
  deleteSession,
  setActiveTab,
  setIsSettingsOpen,
  setView,
  onLogout,
  onOpenUpdates,
  onOpenDocs,
  onProfile,
  webUsage,
  apiUsage,
  onAccountMenuChange,
}) {
  const displayName = user?.username || (user?.id ? `用户${String(user.id).slice(-4)}` : "账户");
  const displayInitial = displayName.trim()[0] || "L";
  const touchSelectRef = useRef(null);
  const accountRef = useRef(null);
  const [accountOpen, setAccountOpen] = useState(false);
  const closeAccount = useCallback(() => setAccountOpen(false), []);
  useEffect(() => { onAccountMenuChange?.(accountOpen); }, [accountOpen, onAccountMenuChange]);

  const selectSession = (sessionId, forceClose = false) => {
    if (forceClose) setSidebarOpen(false);
    loadSession(sessionId);
  };

  const handleTouchSession = (event, sessionId) => {
    event.preventDefault();
    event.stopPropagation();
    touchSelectRef.current = sessionId;
    selectSession(sessionId, true);
    window.setTimeout(() => {
      if (touchSelectRef.current === sessionId) touchSelectRef.current = null;
    }, 450);
  };

  return (
    <aside className={`app-sidebar ${sidebarOpen ? "sidebar-open" : ""}`}>
      <div className="sidebar-header">
        <div className="sidebar-brand">
          <div className="brand-mark">
            {ICON_CONFIG.logo ? <img src={ICON_CONFIG.logo} alt="LumaNova" /> : <Zap size={18} />}
          </div>
          <span>LumaNova</span>
        </div>
        <button type="button" className="icon-button" onClick={() => setSidebarOpen(false)} aria-label="收起侧边栏">
          <PanelRightClose size={19} />
        </button>
      </div>

      <div className="sidebar-content">
        <button type="button" className="new-chat-button" onClick={handleNewChat}>
          <Plus size={18} />
          <span>新对话</span>
        </button>
        <div className="session-list">
          {sessions.map((session) => {
            const isPending = pendingSessionIds.has(session.session_id);
            const isUnread = unreadSessionIds.has(session.session_id);
            return (
              <div
                key={session.session_id}
                className={`session-row ${activeSessionId === session.session_id ? "active" : ""} ${isPending ? "pending" : ""} ${isUnread ? "unread" : ""}`}
              >
                <button
                  type="button"
                  className="session-title-button"
                  onPointerUp={(event) => {
                    if (event.pointerType && event.pointerType !== "mouse") {
                      handleTouchSession(event, session.session_id);
                    }
                  }}
                  onTouchEnd={(event) => {
                    if (touchSelectRef.current !== session.session_id) {
                      handleTouchSession(event, session.session_id);
                    }
                  }}
                  onClick={(event) => {
                    if (touchSelectRef.current === session.session_id) {
                      event.preventDefault();
                      return;
                    }
                    selectSession(session.session_id);
                  }}
                >
                  <span>{session.title || "新对话"}</span>
                  {isPending && <i className="session-running" aria-label="生成中" />}
                  {!isPending && isUnread && <i className="session-unread" aria-label="有新回复" />}
                </button>
                <button
                  type="button"
                  className="session-delete"
                  onClick={(event) => {
                    event.stopPropagation();
                    deleteSession(session.session_id);
                  }}
                  aria-label="删除会话"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <div className="sidebar-footer">
        <button type="button" className="sidebar-action" onClick={onOpenDocs}>
          <BookOpen size={18} />
          <span>开发文档</span>
        </button>
        <button type="button" className="sidebar-action" onClick={onOpenUpdates}>
          <Bell size={18} />
          <span>更新公告</span>
        </button>
        <button
          type="button"
          className="account-button"
          ref={accountRef}
          aria-expanded={accountOpen}
          aria-haspopup="dialog"
          onClick={() => setAccountOpen(!accountOpen)}
        >
          <span className="user-initial">{displayInitial}</span>
          <span className="user-name">{displayName}</span>
          <ChevronUp size={18} />
        </button>
      </div>
      {accountOpen && <AccountMenu user={user} isDark={isDark} anchorRef={accountRef}
        webUsage={webUsage} apiUsage={apiUsage} onClose={closeAccount} onProfile={onProfile}
        onSettings={tab=>{ setActiveTab(tab); setIsSettingsOpen(true); }}
        onLogout={onLogout || (()=>setView("login"))} />}
    </aside>
  );
}
